/**
 * RawDataWidget — JSON viewer for any TheOddsAPI response
 * Extracted from: updateRawData/clearRawData/copyRawData/exportAllData lines 2879-2916
 * Allows fetching any endpoint and viewing/copying/exporting the raw JSON.
 * API: Any endpoint (user-selected)
 * CSS prefix: rdw-
 */

import { useState, useCallback } from 'react'
import { fetchFromOddsProxy } from '../lib/api'
import { Database, Copy, Download, Trash2, RefreshCw, ChevronRight, Send } from 'lucide-react'
import './RawDataWidget.css'

const ENDPOINTS = [
  { path: '/sports', label: 'Sports (FREE)', params: '' },
  { path: '/events/', label: 'Events (FREE)', needsSport: true, params: '' },
  { path: '/odds/', label: 'Odds (TOKENS)', needsSport: true, params: 'regions=us&markets=h2h' },
  { path: '/scores/', label: 'Scores (1-2 tokens)', needsSport: true, params: 'daysFrom=1' },
  { path: '/participants/', label: 'Participants (1 token)', needsSport: true, params: '' },
  { path: '/status', label: 'Worker Status (FREE)', params: '' },
]

function RawDataWidget() {
  const [expanded, setExpanded] = useState(true)
  const [rawData, setRawData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedEndpoint, setSelectedEndpoint] = useState(0)
  const [sportKey, setSportKey] = useState('baseball_mlb')
  const [customParams, setCustomParams] = useState('')
  const [lastFetch, setLastFetch] = useState(null)
  const [copyFeedback, setCopyFeedback] = useState(false)

  const fetchData = useCallback(async () => {
    const ep = ENDPOINTS[selectedEndpoint]
    let path = ep.path
    if (ep.needsSport) path += sportKey

    setLoading(true)
    setError(null)

    // Parse custom params
    const params = {}
    const paramStr = customParams || ep.params
    if (paramStr) {
      paramStr.split('&').forEach(pair => {
        const [k, v] = pair.split('=')
        if (k && v) params[k.trim()] = v.trim()
      })
    }

    const res = await fetchFromOddsProxy(path, params)

    if (res.status === 'ok' && res.data) {
      setRawData(res.data)
      setLastFetch(new Date())
    } else {
      setError(res.message || 'Request failed')
      setRawData(res.data || { error: res.message })
    }

    setLoading(false)
  }, [selectedEndpoint, sportKey, customParams])

  const clearData = () => {
    setRawData(null)
    setError(null)
    setLastFetch(null)
  }

  const copyData = async () => {
    if (!rawData) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(rawData, null, 2))
      setCopyFeedback(true)
      setTimeout(() => setCopyFeedback(false), 2000)
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea')
      ta.value = JSON.stringify(rawData, null, 2)
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setCopyFeedback(true)
      setTimeout(() => setCopyFeedback(false), 2000)
    }
  }

  const exportData = () => {
    if (!rawData) return
    const blob = new Blob([JSON.stringify(rawData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `odds_data_${new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19)}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const ep = ENDPOINTS[selectedEndpoint]
  const dataSize = rawData ? JSON.stringify(rawData).length : 0
  const recordCount = Array.isArray(rawData) ? rawData.length : null

  return (
    <div className="widget-card">
      <div
        className="widget-header rdw-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <Database size={16} className="widget-icon" />
        <span className="widget-title">Raw Data</span>
        {rawData && (
          <span className="rdw-size">
            {recordCount !== null ? `${recordCount} records` : ''}
            {dataSize > 0 ? ` (${(dataSize / 1024).toFixed(1)} KB)` : ''}
          </span>
        )}
        <ChevronRight
          size={16}
          className={`rdw-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          {/* Controls */}
          <div className="rdw-controls">
            <select
              className="rdw-select"
              value={selectedEndpoint}
              onChange={(e) => {
                const idx = Number(e.target.value)
                setSelectedEndpoint(idx)
                setCustomParams(ENDPOINTS[idx].params)
              }}
            >
              {ENDPOINTS.map((ep, i) => (
                <option key={i} value={i}>{ep.label}</option>
              ))}
            </select>

            {ep.needsSport && (
              <input
                type="text"
                className="rdw-input"
                placeholder="Sport key (e.g. baseball_mlb)"
                value={sportKey}
                onChange={(e) => setSportKey(e.target.value)}
              />
            )}

            <input
              type="text"
              className="rdw-input rdw-params-input"
              placeholder="Query params (e.g. regions=us&markets=h2h)"
              value={customParams}
              onChange={(e) => setCustomParams(e.target.value)}
            />

            <button
              className="rdw-send-btn"
              onClick={fetchData}
              disabled={loading || (ep.needsSport && !sportKey)}
            >
              {loading ? <RefreshCw size={13} className="spinning" /> : <Send size={13} />}
              <span>Fetch</span>
            </button>
          </div>

          {/* Action bar */}
          {rawData && (
            <div className="rdw-actions">
              <button className="rdw-action-btn" onClick={copyData}>
                <Copy size={12} />
                <span>{copyFeedback ? 'Copied!' : 'Copy'}</span>
              </button>
              <button className="rdw-action-btn" onClick={exportData}>
                <Download size={12} />
                <span>Export JSON</span>
              </button>
              <button className="rdw-action-btn rdw-clear" onClick={clearData}>
                <Trash2 size={12} />
                <span>Clear</span>
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {/* JSON viewer */}
          {rawData && (
            <pre className="rdw-json">{JSON.stringify(rawData, null, 2)}</pre>
          )}

          {/* Empty state */}
          {!rawData && !loading && (
            <div className="widget-body">
              <div className="widget-empty">
                Select an endpoint and click Fetch to view raw API response data.
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="widget-footer">
            <span>{lastFetch ? `Fetched ${lastFetch.toLocaleTimeString()}` : 'No data'}</span>
            <span>{ep.needsSport ? `${ep.path}${sportKey}` : ep.path}</span>
          </div>
        </>
      )}
    </div>
  )
}

export default RawDataWidget
